// @flow
import * as React from "react";
import moment from "moment";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import { fontSize } from "../common/styled-mixins";
import StockName from "./StockName";

import type { Stock, Account } from "../../redux/CommonTypes";
import { gaReducer } from "../GA";

const TableRow = styled.div`
  direction: ${({ theme }) => theme.direction};
  position: relative;
  display: flex;
  flex-flow: row wrap;
  height: 37px;
  margin-bottom: 3px;
  font-size: 12px;
  color: #99959d;
  text-align: center;
  line-height: 33px;
  cursor: pointer;

  @media (min-width: 960px) {
    padding-left: 9px;
    padding-right: 21px;
  }

  @media (max-width: 959px) {
    height: auto;
  }

  ${(props) => {
    switch (true) {
      case props.disabled && props.willReopen:
        return css`
          opacity: 0.5;
          cursor: default;

          :hover {
            opacity: 1;
            color: ${({ theme }) => theme.accent};
            ${Disabled} {
              color: #0085eb;
            }

            > div:last-of-type {
              display: flex;
            }
          }
        `;
      case props.disabled:
        return css`
          opacity: 0.5;
          cursor: default;
        `;
      default:
        return css`
          :hover {
            color: ${props.theme.accent};
          }
        `;
    }
  }};

  :after {
    ${({ tourBorder }) =>
      tourBorder &&
      css`
        position: absolute;
        left: -10px;
        top: -10px;
        right: -10px;
        bottom: -10px;
        border-radius: 6px;
        ${tourBorder.border};
        content: "";
      `};

    ${({ tourBorderAnimation }) =>
      tourBorderAnimation &&
      tourBorderAnimation.animation(tourBorderAnimation.keyframe)};
  }
`;

const TableCell = styled.div`
  position: relative;
  flex: 1;
  flex-basis: 50%;
  padding-left: 7px;
  padding-right: 7px;
  vertical-align: middle;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 336px) {
    padding-right: 16px;
  } 

  ${(props) =>
    props.description &&
    css`
      flex-basis: 33.3333%;
    `};
    
  ${(props) =>
  props.stock &&
  css`
    text-align:left;
  `};
`;

const Disabled = styled.span`
  position: absolute;
  top: 8px;
  right: -2px;
  font-size: 0;
  line-height: 0;
  z-index: 2;
`;

const Info = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 2px;
  display: none;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding-left: 21px;
  padding-right: 21px;
  font-size: ${fontSize(10)};
  color: ${({ theme }) => theme.foreground};
  background-color: ${({ theme }) => theme.stockInfoBg};
  box-shadow: 7px 0 0 0 ${({ theme }) => theme.stockInfoBg};
  z-index: 1;

  @media (max-width: 959px) {
    position: absolute;
    top: 0;
    left: auto;
    right: 28px;
    height: 33px;
  }

  @media (max-width: 479px) {
    position: static;
    width: 100%;
    height: 15px;
  }
`;

const InfoDate = styled.div`
  color: ${({ theme }) => theme.lightAccent};
`;

const Lock = () => (
  <svg
    fill="currentColor"
    height="16"
    viewBox="0 0 24 24"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M0 0h24v24H0z" fill="none" />
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

type Props = {
  item: Stock,
  activeAccount: Account,
  onClick: (SyntheticMouseEvent<*>) => void,
};

function StockRow(props: Props) {
  const handleClick = (event: SyntheticMouseEvent<*>) => {
    if (props.item.enabled) {
      gaReducer("CLICKBTN_STOCK", {
        buttonText: props.item.stock,
      });
      props.onClick(event);
    }
  };

  const isNew = props.item.is_new;

  let { title, description, enabled } = props.item;
  let profit;

  if (props.tradeMode === "tournament") {
    profit = props.item.realProfit - props.item.profit_vip;
  } else {
    profit =
      props.activeAccount.type === "real"
        ? props.item.realProfit
        : props.item.demoProfit;
  }

  return (
    <TableRow
      ref={props.forwardedRef}
      disabled={!props.item.enabled}
      willReopen={props.item.state_changed_at != null}
      onClick={handleClick}
      tourBorder={!props.isOriginal && props.tour && props.tour.borderStyles}
      tourBorderAnimation={
        !props.isOriginal &&
        props.tour &&
        props.tour.outside &&
        props.tour.borderStyles
      }
    >
      <TableCell stock={true} description={props.isNameColumnVisible && description}>
        <StockName isNew={isNew} name={title} />
      </TableCell>
      {description != null && props.isNameColumnVisible && (
        <TableCell
          description={props.isNameColumnVisible && description}
          title={description}
        >
          {description}
        </TableCell>
      )}
      <TableCell description={props.isNameColumnVisible && description}>
        {profit}%
        {!enabled && (
          <Disabled description={description}>
            <Lock />
          </Disabled>
        )}
      </TableCell>
      <Info description={props.isNameColumnVisible && description}>
        <Gettext>Opening</Gettext>
        :&nbsp;
        <InfoDate>
          {props.item.state_changed_at != null
            ? moment(props.item.state_changed_at * 1000).format("MMM Do, HH:mm")
            : null}
        </InfoDate>
      </Info>
    </TableRow>
  );
}

export default React.forwardRef((props, ref) => (
  <StockRow {...props} forwardedRef={ref} />
));



// WEBPACK FOOTER //
// ./src/components/StockMenu/StockRow.js